import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent
} from '@mui/material';

import {
  CheckCircle,
  Cancel,
  Schedule,
  Warning,
  Refresh,
  Add,
  Visibility,
  Delete
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useRole } from '../contexts/RoleContext';
import { useUserTimezone } from '../hooks/useUserTimezone';
import { getApiUrl } from '../utils/api';

interface Customer {
  id: number;
  name: string;
  phone_number: string;
  caregiver_name: string;
  caregiver_phone: string;
  caregiver_email: string;
  is_active: boolean;
  has_valid_subscription?: boolean | null;
  subscription_status?: string | null;
  subscription_valid_until?: string | null;
  created_at: string;
  updated_at: string | null;
}

interface SMSRateLimitInfo {
  phone_number: string;
  normalized_phone: string;
  current_count: number;
  check_in_messages: number;
  alert_messages: number;
  rate_limit: number;
  remaining_messages: number;
  is_allowed: boolean;
  cutoff_time: string;
}

interface CheckInAttempt {
  id: number;
  customer_id: number;
  status: string;
  retry_count: number;
  max_retries?: number;
  created_at: string;
  next_retry_time?: string;
}

export const getLatestCheckInIdsByCustomer = (
  checkIns: CheckInAttempt[]
): Record<number, number> => {
  const latestByCustomer: Record<number, CheckInAttempt> = {};

  for (const checkIn of checkIns) {
    const current = latestByCustomer[checkIn.customer_id];
    if (!current) {
      latestByCustomer[checkIn.customer_id] = checkIn;
      continue;
    }

    const currentTs = new Date(current.created_at).getTime();
    const nextTs = new Date(checkIn.created_at).getTime();
    if (nextTs > currentTs || (nextTs === currentTs && checkIn.id > current.id)) {
      latestByCustomer[checkIn.customer_id] = checkIn;
    }
  }

  return Object.fromEntries(
    Object.entries(latestByCustomer).map(([customerId, checkIn]) => [
      Number(customerId),
      checkIn.id,
    ])
  );
};

export const shouldShowCancelAlertsAction = (
  checkIn: CheckInAttempt,
  latestCheckInIdsByCustomer: Record<number, number>,
  customersWithActiveAlerts: Set<number>
): boolean => {
  const isLatestCheckInForCustomer =
    latestCheckInIdsByCustomer[checkIn.customer_id] === checkIn.id;
  return isLatestCheckInForCustomer && customersWithActiveAlerts.has(checkIn.customer_id);
};

interface Alert {
  id: number;
  customer_id: number;
  customer_name?: string;
  alert_type: string;
  severity: string;
  status: string;
  title: string;
  message: string;
  created_at: string;
  acknowledged_at?: string;
  resolved_at?: string;
}

interface DashboardData {
  customers: Customer[];
  todayCheckIns: CheckInAttempt[];
  recentAlerts: Alert[];
  stats: {
    totalCustomers: number;
    todayResponses: number;
    pendingAlerts: number;
    activeCheckIns: number;
  };
}

const Dashboard: React.FC = () => {
  const { token } = useAuth();
  const { isAdmin } = useRole();
  const { timezone: userTimezone } = useUserTimezone();
  const [data, setData] = useState<DashboardData | null>(null);

  // Helper function to format timestamps in user's timezone
  const formatTimestamp = (timestamp: string) => {
    try {
      const date = new Date(timestamp);
      return date.toLocaleString('en-US', {
        timeZone: userTimezone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: true
      });
    } catch (error) {
      console.error('Error formatting timestamp:', error);
      return new Date(timestamp).toLocaleString();
    }
  };

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [refreshLoading, setRefreshLoading] = useState(false);
  const [sendingCheckIn, setSendingCheckIn] = useState<number | null>(null);
  const [cancellingAlert, setCancellingAlert] = useState<number | null>(null);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [customerDialogOpen, setCustomerDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [customerToDelete, setCustomerToDelete] = useState<Customer | null>(null);
  const [deactivateDialogOpen, setDeactivateDialogOpen] = useState(false);
  const [customerToDeactivate, setCustomerToDeactivate] = useState<Customer | null>(null);
  const [smsCounts, setSmsCounts] = useState<Record<string, SMSRateLimitInfo>>({});
  const [loadingSmsCounts, setLoadingSmsCounts] = useState<Record<string, boolean>>({});
  const [accountBalance, setAccountBalance] = useState<{
    available: boolean;
    error: string | null;
    balance: number | null;
    currency: string | null;
    sufficient_funds: boolean;
  } | null>(null);
  const [orphanedUsers, setOrphanedUsers] = useState<Array<{
    id: number;
    email: string;
    full_name: string | null;
    phone_number: string | null;
    is_active: boolean;
    created_at: string | null;
  }>>([]);
  const [purgingOrphanId, setPurgingOrphanId] = useState<number | null>(null);
  const [newCustomer, setNewCustomer] = useState({
    name: '',
    phone_number: '',
    caregiver_name: '',
    caregiver_phone: '',
    caregiver_email: ''
  });

  const fetchOrphanedUsers = async () => {
    if (!isAdmin) {
      setOrphanedUsers([]);
      return;
    }
    try {
      const API_URL = getApiUrl();
      const response = await fetch(`${API_URL}/admin/orphaned-users`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      if (response.ok) {
        const data = await response.json();
        setOrphanedUsers(Array.isArray(data) ? data : []);
      }
    } catch (err) {
      console.error('Error fetching orphaned users:', err);
    }
  };

  const purgeOrphanedUser = async (userId: number, email: string) => {
    if (
      !window.confirm(
        `Permanently delete incomplete signup for ${email}? This frees the email/phone for a new signup.`
      )
    ) {
      return;
    }
    try {
      setPurgingOrphanId(userId);
      const API_URL = getApiUrl();
      const response = await fetch(`${API_URL}/admin/orphaned-users/${userId}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      if (response.ok) {
        setSuccess(`Deleted incomplete signup: ${email}`);
        setTimeout(() => setSuccess(null), 3000);
        await fetchOrphanedUsers();
      } else {
        const body = await response.json().catch(() => ({}));
        setError(body?.detail || 'Failed to delete incomplete signup');
      }
    } catch (err) {
      setError('Failed to delete incomplete signup');
    } finally {
      setPurgingOrphanId(null);
    }
  };

  const fetchAccountBalance = async () => {
    try {
      const API_URL = getApiUrl();
      const response = await fetch(`${API_URL}/sms/account-balance`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const balanceData = await response.json();
        setAccountBalance(balanceData);
      } else {
        // If endpoint fails, set default values
        setAccountBalance({
          available: false,
          error: 'Unable to check balance',
          balance: null,
          currency: null,
          sufficient_funds: false
        });
      }
    } catch (err) {
      console.error('Error fetching account balance:', err);
      setAccountBalance({
        available: false,
        error: 'Error checking balance',
        balance: null,
        currency: null,
        sufficient_funds: false
      });
    }
  };

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);

      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };

      const API_URL = getApiUrl();
      
      // Fetch customers
      const customersResponse = await fetch(`${API_URL}/customers/?_t=${Date.now()}`, { 
        headers: {
          ...headers,
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache'
        }
      });
      if (!customersResponse.ok) {
        if (customersResponse.status === 401 || customersResponse.status === 403) {
          throw new Error('Your session expired. Please log out and log back in.');
        }
        throw new Error(`Failed to fetch customers: ${customersResponse.status}`);
      }
      const customers = await customersResponse.json();
      console.log('Customers response:', customers);

      // Fetch today's check-ins
      const today = new Date().toISOString().split('T')[0];
      const checkInsResponse = await fetch(`${API_URL}/sms/check-in-history?date=${today}&_t=${Date.now()}`, { 
        headers: {
          ...headers,
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache'
        }
      });
      const checkInsData = checkInsResponse.ok ? await checkInsResponse.json() : { check_in_history: [] };
      const todayCheckIns = checkInsData.check_in_history || [];

      // Fetch recent alerts (last 7 days) - increased limit to show all alerts
      const alertsResponse = await fetch(`${API_URL}/alerts/?limit=50&_t=${Date.now()}`, { 
        headers: {
          ...headers,
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache'
        }
      });
      const alertsData = alertsResponse.ok ? await alertsResponse.json() : { alerts: [] };
      const recentAlerts = alertsData.alerts || [];

      // Calculate stats
      const stats = {
        totalCustomers: Array.isArray(customers) ? customers.length : 0,
        todayResponses: Array.isArray(todayCheckIns) ? todayCheckIns.filter((ci: CheckInAttempt) => ci.status === 'responded').length : 0,
        pendingAlerts: Array.isArray(recentAlerts) ? recentAlerts.filter((a: Alert) => a.status === 'active' || a.status === 'escalated').length : 0,
        activeCheckIns: Array.isArray(todayCheckIns) ? todayCheckIns.filter((ci: CheckInAttempt) => ci.status === 'sent' || ci.status === 'pending').length : 0
      };

      const dataObject = {
        customers: Array.isArray(customers) ? customers : [],
        todayCheckIns: Array.isArray(todayCheckIns) ? todayCheckIns : [],
        recentAlerts: Array.isArray(recentAlerts) ? recentAlerts : [],
        stats
      };
      console.log('Setting data:', dataObject);
      setData(dataObject);
    } catch (err) {
      console.error('Error fetching dashboard data:', err);
      setError('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshLoading(true);
    await fetchDashboardData();
    setRefreshLoading(false);
  };

  const handleSendCheckIn = async (customerId: number) => {
    try {
      setSendingCheckIn(customerId);
      setError(null);
      
      const API_URL = getApiUrl();
      const response = await fetch(`${API_URL}/sms/send-check-in/${customerId}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        // Refresh data after sending check-in
        await fetchDashboardData();
        // Show success message
        setSuccess('Check-in sent successfully!');
        // Clear success message after 3 seconds
        setTimeout(() => setSuccess(null), 3000);
      } else {
        let message = 'Failed to send check-in';
        try {
          const body = await response.json();
          if (body?.detail) {
            message = typeof body.detail === 'string' ? body.detail : message;
          }
        } catch {
          // keep default message
        }
        if (response.status === 402) {
          message = `${message}. Set up or renew billing under My Profile, or contact support.`;
        }
        setError(message);
      }
    } catch (err) {
      console.error('Error sending check-in:', err);
      setError('Failed to send check-in');
    } finally {
      setSendingCheckIn(null);
    }
  };

  const handleCancelAlerts = async (customerId: number) => {
    try {
      setCancellingAlert(customerId);
      setError(null);
      
      const API_URL = getApiUrl();
      const response = await fetch(`${API_URL}/alerts/customer/${customerId}/cancel-all`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const result = await response.json();
        // Refresh data after cancelling alerts
        await fetchDashboardData();
        // Show success message
        setSuccess(`Cancelled ${result.cancelled_count} active alerts for customer`);
        // Clear success message after 3 seconds
        setTimeout(() => setSuccess(null), 3000);
      } else {
        setError('Failed to cancel alerts');
      }
    } catch (err) {
      console.error('Error cancelling alerts:', err);
      setError('Failed to cancel alerts');
    } finally {
      setCancellingAlert(null);
    }
  };

  const handleCancelCheckIns = async (customerId: number) => {
    try {
      setCancellingAlert(customerId);
      setError(null);
      
      const API_URL = getApiUrl();
      const response = await fetch(`${API_URL}/sms/cancel-check-ins/${customerId}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        // Refresh data after cancelling check-ins
        await fetchDashboardData();
        // Show success message
        setSuccess('Cancelled active check-ins for customer');
        // Clear success message after 3 seconds
        setTimeout(() => setSuccess(null), 3000);
      } else {
        setError('Failed to cancel check-ins');
      }
    } catch (err) {
      console.error('Error cancelling check-ins:', err);
      setError('Failed to cancel check-ins');
    } finally {
      setCancellingAlert(null);
    }
  };

  const handleCreateCustomer = async () => {
    try {
      const API_URL = getApiUrl();
      const response = await fetch(`${API_URL}/customers/`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(newCustomer)
      });

      if (response.ok) {
        setCustomerDialogOpen(false);
        setNewCustomer({ name: '', phone_number: '', caregiver_name: '', caregiver_phone: '', caregiver_email: '' });
        await fetchDashboardData();
      } else {
        setError('Failed to create customer');
      }
    } catch (err) {
      console.error('Error creating customer:', err);
      setError('Failed to create customer');
    }
  };

  const handleDeleteCustomer = async () => {
    if (!customerToDelete) return;
    
    try {
      // Use hard_delete=true to permanently delete
      const API_URL = getApiUrl();
      const response = await fetch(`${API_URL}/customers/${customerToDelete.id}?hard_delete=true`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        setDeleteDialogOpen(false);
        setCustomerToDelete(null);
        await fetchDashboardData();
        setSuccess('Customer deleted successfully!');
        setTimeout(() => setSuccess(null), 3000);
      } else {
        // Try to parse JSON error, but handle non-JSON responses
        let errorMessage = 'Failed to delete customer';
        const contentType = response.headers.get('content-type');
        const isJson = contentType && contentType.includes('application/json');
        
        try {
          if (isJson) {
            const errorData = await response.json();
            errorMessage = errorData.detail || errorMessage;
          } else {
            // Read text response (can only read once)
            const text = await response.text();
            errorMessage = text || errorMessage;
          }
        } catch (parseErr) {
          // If parsing fails, just use default message
          console.error('Error parsing response:', parseErr);
        }
        
        // Check if deletion actually succeeded despite the error
        // Sometimes backend returns 500 but deletion succeeds
        try {
          await fetchDashboardData();
          const stillExists = data?.customers?.some(c => c.id === customerToDelete?.id);
          if (!stillExists) {
            // Customer was actually deleted, treat as success
            setDeleteDialogOpen(false);
            setCustomerToDelete(null);
            setSuccess('Customer deleted successfully!');
            setTimeout(() => setSuccess(null), 3000);
            return;
          }
        } catch (fetchErr) {
          // Ignore fetch errors in error handler
          console.error('Error checking if customer still exists:', fetchErr);
        }
        
        setError(errorMessage);
      }
    } catch (err) {
      console.error('Error deleting customer:', err);
      // Check if deletion actually succeeded despite the error
      try {
        await fetchDashboardData();
        const stillExists = data?.customers?.some(c => c.id === customerToDelete?.id);
        if (!stillExists) {
          // Customer was actually deleted, treat as success
          setDeleteDialogOpen(false);
          setCustomerToDelete(null);
          setSuccess('Customer deleted successfully!');
          setTimeout(() => setSuccess(null), 3000);
          return;
        }
      } catch (fetchErr) {
        // Ignore fetch errors in error handler
        console.error('Error checking if customer still exists:', fetchErr);
      }
      setError('Failed to delete customer');
    }
  };

  const openDeleteDialog = (customer: Customer) => {
    setCustomerToDelete(customer);
    setDeleteDialogOpen(true);
  };

  const openDeactivateDialog = (customer: Customer) => {
    setCustomerToDeactivate(customer);
    setDeactivateDialogOpen(true);
  };

  const handleDeactivateCustomer = async () => {
    if (!customerToDeactivate) return;
    
    try {
      const API_URL = getApiUrl();
      const response = await fetch(`${API_URL}/customers/${customerToDeactivate.id}/deactivate`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        setDeactivateDialogOpen(false);
        setCustomerToDeactivate(null);
        await fetchDashboardData();
        setSuccess('Customer deactivated successfully! They will no longer receive alerts.');
        setTimeout(() => setSuccess(null), 3000);
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Failed to deactivate customer');
      }
    } catch (err) {
      console.error('Error deactivating customer:', err);
      setError('Failed to deactivate customer');
    }
  };

  const handleActivateCustomer = async (customer: Customer) => {
    try {
      const API_URL = getApiUrl();
      const response = await fetch(`${API_URL}/customers/${customer.id}/activate`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        await fetchDashboardData();
        setSuccess('Customer activated successfully! They will resume receiving alerts.');
        setTimeout(() => setSuccess(null), 3000);
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Failed to activate customer');
      }
    } catch (err) {
      console.error('Error activating customer:', err);
      setError('Failed to activate customer');
    }
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'responded':
        return 'success';
      case 'pending':
        return 'warning';
      case 'missed':
        return 'error';
      default:
        return 'default';
    }
  };

  const getSubscriptionChipColor = (customer: Customer) => {
    const status = (customer.subscription_status || '').toLowerCase();
    if (customer.has_valid_subscription) return 'success';
    if (status === 'past_due' || status === 'unpaid') return 'warning';
    if (status === 'cancelled' || status === 'inactive' || status === 'none') return 'default';
    return 'default';
  };

  const getSubscriptionLabel = (customer: Customer) => {
    const status = (customer.subscription_status || '').toLowerCase();
    if (customer.has_valid_subscription) return 'Valid';
    if (status === 'legacy') return 'Legacy (active)';
    if (status === 'none') return 'No Subscription';
    if (status) return status.replace('_', ' ');
    return 'Unknown';
  };

  const getSeverityColor = (severity: string) => {
    switch (severity.toLowerCase()) {
      case 'high':
        return 'error';
      case 'medium':
        return 'warning';
      case 'low':
        return 'info';
      default:
        return 'default';
    }
  };

  const fetchSmsCountForPhone = async (phoneNumber: string) => {
    if (loadingSmsCounts[phoneNumber]) return; // Avoid duplicate requests
    
    try {
      setLoadingSmsCounts(prev => ({ ...prev, [phoneNumber]: true }));
      const API_URL = getApiUrl();
      const response = await fetch(`${API_URL}/sms/rate-limit-check/${encodeURIComponent(phoneNumber)}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data: SMSRateLimitInfo = await response.json();
        setSmsCounts(prev => ({ ...prev, [phoneNumber]: data }));
      } else {
        console.error(`Failed to fetch SMS count for ${phoneNumber}`);
      }
    } catch (err) {
      console.error(`Error fetching SMS count for ${phoneNumber}:`, err);
    } finally {
      setLoadingSmsCounts(prev => ({ ...prev, [phoneNumber]: false }));
    }
  };

  const fetchAllSmsCounts = async () => {
    if (!data?.customers) return;
    
    // Fetch SMS counts for all customer phone numbers
    const phoneNumbers = data.customers
      .map(c => c.phone_number)
      .filter(phone => phone && !smsCounts[phone]); // Only fetch if not already loaded
    
    // Fetch in parallel (limit to 5 at a time to avoid overwhelming the API)
    const batchSize = 5;
    for (let i = 0; i < phoneNumbers.length; i += batchSize) {
      const batch = phoneNumbers.slice(i, i + batchSize);
      await Promise.all(batch.map(phone => fetchSmsCountForPhone(phone)));
    }
  };

  useEffect(() => {
    fetchDashboardData();

    // Plivo balance is admin-only — never call or surface for regular users
    if (isAdmin) {
      fetchAccountBalance();
      fetchOrphanedUsers();
    } else {
      setAccountBalance(null);
      setOrphanedUsers([]);
    }

    // Set up polling to refresh dashboard data every 3 seconds for real-time updates
    const pollInterval = setInterval(() => {
      // Only poll if not currently loading to avoid overlapping requests
      if (!loading) {
        fetchDashboardData();
      }
    }, 3000); // Poll every 3 seconds

    const balanceInterval = isAdmin
      ? setInterval(() => {
          fetchAccountBalance();
        }, 300000)
      : null;

    return () => {
      clearInterval(pollInterval);
      if (balanceInterval) {
        clearInterval(balanceInterval);
      }
    };
  }, [token, isAdmin]);

  // Fetch SMS counts when customer data is loaded
  useEffect(() => {
    if (data?.customers && data.customers.length > 0) {
      fetchAllSmsCounts();
    }
  }, [data?.customers]);

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Box>
    );
  }

  const latestCheckInIdsByCustomer = getLatestCheckInIdsByCustomer(data?.todayCheckIns || []);
  const customersWithActiveAlerts = new Set(
    (data?.recentAlerts || [])
      .filter((alert) => alert.status === 'active')
      .map((alert) => alert.customer_id)
  );

  return (
    <Box sx={{ p: 0, m: 0 }}>
      {/* Account Balance Alert — admin only */}
      {isAdmin && accountBalance && !accountBalance.sufficient_funds && accountBalance.available && (
        <Alert 
          severity="error" 
          sx={{ mb: 2 }}
          action={
            <Button 
              color="inherit" 
              size="small" 
              onClick={fetchAccountBalance}
              sx={{ textTransform: 'none' }}
            >
              Refresh
            </Button>
          }
        >
          <Box>
            <Typography variant="body1" fontWeight="bold">
              ⚠️ Insufficient Plivo Account Funds
            </Typography>
            <Typography variant="body2" sx={{ mt: 0.5 }}>
              Your Plivo account balance is {accountBalance.balance !== null 
                ? `${accountBalance.currency || 'USD'} ${accountBalance.balance.toFixed(2)}`
                : 'unknown'}. 
              SMS check-ins cannot be sent until you add credits to your Plivo account.
            </Typography>
            <Typography variant="body2" sx={{ mt: 0.5, fontSize: '0.875rem' }}>
              Please visit{' '}
              <a 
                href="https://console.plivo.com" 
                target="_blank" 
                rel="noopener noreferrer"
                style={{ color: 'inherit', textDecoration: 'underline' }}
              >
                Plivo Console
              </a>
              {' '}to add credits.
            </Typography>
          </Box>
        </Alert>
      )}
      {isAdmin && accountBalance && !accountBalance.available && accountBalance.error && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Unable to check Plivo account balance: {accountBalance.error}
        </Alert>
      )}
      {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {success}
          </Alert>
        )}

      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          Wellness Check Dashboard
        </Typography>
        <Box>
          {isAdmin && (
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => setCustomerDialogOpen(true)}
              sx={{ mr: 1 }}
            >
              Add Customer
            </Button>
          )}
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={handleRefresh}
            disabled={refreshLoading}
          >
            {refreshLoading ? <CircularProgress size={20} /> : 'Refresh'}
          </Button>
        </Box>
      </Box>

      {/* Stats Cards */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }, gap: 3, mb: 3 }}>
        <Card>
          <CardContent>
            <Typography color="textSecondary" gutterBottom>
              Total Customers
            </Typography>
            <Typography variant="h4">
              {data?.stats.totalCustomers || 0}
            </Typography>
          </CardContent>
        </Card>
        <Card>
          <CardContent>
            <Typography color="textSecondary" gutterBottom>
              Today's Responses
            </Typography>
            <Typography variant="h4" color="success.main">
              {data?.stats.todayResponses || 0}
            </Typography>
          </CardContent>
        </Card>
        <Card>
          <CardContent>
            <Typography color="textSecondary" gutterBottom>
              Pending Alerts
            </Typography>
            <Typography variant="h4" color="warning.main">
              {data?.stats.pendingAlerts || 0}
            </Typography>
          </CardContent>
        </Card>
        <Card>
          <CardContent>
            <Typography color="textSecondary" gutterBottom>
              Active Check-ins
            </Typography>
            <Typography variant="h4" color="info.main">
              {data?.stats.activeCheckIns || 0}
            </Typography>
          </CardContent>
        </Card>
      </Box>

      {/* Today's Check-ins and Recent Alerts */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 3 }}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Today's Check-in Status
            </Typography>
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Customer</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Time</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data?.todayCheckIns.map((checkIn) => {
                    const customer = data.customers.find(c => c.id === checkIn.customer_id);
                    const isLatestCheckInForCustomer =
                      latestCheckInIdsByCustomer[checkIn.customer_id] === checkIn.id;
                    const hasActiveAlertForCustomer = customersWithActiveAlerts.has(checkIn.customer_id);
                    return (
                      <TableRow key={checkIn.id}>
                        <TableCell>{customer?.name || 'Unknown'}</TableCell>
                        <TableCell>
                          <Chip
                            label={checkIn.status}
                            color={getStatusColor(checkIn.status) as any}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {formatTimestamp(checkIn.created_at)}
                          </Typography>
                          {checkIn.next_retry_time && (
                            <Typography variant="caption" color="text.secondary" display="block">
                              Next retry: {formatTimestamp(checkIn.next_retry_time)}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                            {checkIn.status === 'pending' && (
                              <Button
                                size="small"
                                variant="outlined"
                                disabled={sendingCheckIn === checkIn.customer_id}
                                onClick={() => handleSendCheckIn(checkIn.customer_id)}
                              >
                                {sendingCheckIn === checkIn.customer_id ? 'Sending...' : 'Send Check-in'}
                              </Button>
                            )}
                            {checkIn.status === 'sent' && (
                              <Box>
                                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 'bold' }}>
                                  Try {checkIn.retry_count + 1} of {(checkIn.max_retries || 5) + 1}
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5 }}>
                                  {[...Array((checkIn.max_retries || 5) + 1)].map((_, index) => (
                                    <Box
                                      key={index}
                                      sx={{
                                        width: 8,
                                        height: 8,
                                        borderRadius: '50%',
                                        backgroundColor: index < checkIn.retry_count + 1 ? '#1976d2' : '#e0e0e0'
                                      }}
                                    />
                                  ))}
                                </Box>
                              </Box>
                            )}
                            {/* Cancel Buttons - Show based on customer state */}
                            {shouldShowCancelAlertsAction(
                              checkIn,
                              latestCheckInIdsByCustomer,
                              customersWithActiveAlerts
                            ) && (
                              <Button
                                size="small"
                                variant="outlined"
                                color="error"
                                disabled={cancellingAlert === checkIn.customer_id}
                                onClick={() => handleCancelAlerts(checkIn.customer_id)}
                                sx={{ mt: 1 }}
                              >
                                {cancellingAlert === checkIn.customer_id ? 'Cancelling...' : 'Cancel Alerts'}
                              </Button>
                            )}
                            {isLatestCheckInForCustomer && checkIn.status === 'sent' && !hasActiveAlertForCustomer && (
                              <Button
                                size="small"
                                variant="outlined"
                                color="warning"
                                disabled={cancellingAlert === checkIn.customer_id}
                                onClick={() => handleCancelCheckIns(checkIn.customer_id)}
                                sx={{ mt: 1 }}
                              >
                                {cancellingAlert === checkIn.customer_id ? 'Cancelling...' : 'Cancel Check-ins'}
                              </Button>
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {(!data?.todayCheckIns || data.todayCheckIns.length === 0) && (
                    <TableRow>
                      <TableCell colSpan={3} align="center">
                        No check-ins today
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>

        {/* Recent Alerts */}
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Recent Alerts
            </Typography>
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Customer</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Severity</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Created</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data?.recentAlerts.map((alert) => (
                    <TableRow key={alert.id}>
                      <TableCell>
                        <Typography variant="body2">
                          {alert.customer_name || 'Unknown'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {alert.alert_type.replace('_', ' ')}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={alert.severity}
                          color={getSeverityColor(alert.severity) as any}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={alert.status}
                          color={alert.status === 'active' ? 'error' : 'success'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {new Date(alert.created_at).toLocaleDateString()}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                  {(!data?.recentAlerts || data.recentAlerts.length === 0) && (
                    <TableRow>
                      <TableCell colSpan={5} align="center">
                        No recent alerts
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      </Box>

      {/* Incomplete Signups — admin only */}
      {isAdmin && orphanedUsers.length > 0 && (
        <Card sx={{ mt: 3, border: '1px solid', borderColor: 'warning.light' }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Incomplete Signups ({orphanedUsers.length})
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Users created during signup who never finished payment. Delete to free email/phone for retry.
            </Typography>
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Phone</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Created</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {orphanedUsers.map((user) => (
                    <TableRow key={user.id}>
                      <TableCell>{user.full_name || 'N/A'}</TableCell>
                      <TableCell>{user.email}</TableCell>
                      <TableCell>{user.phone_number || '—'}</TableCell>
                      <TableCell>
                        <Chip
                          label={user.is_active ? 'Orphan' : 'Inactive orphan'}
                          color="warning"
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        {user.created_at
                          ? new Date(user.created_at).toLocaleDateString()
                          : '—'}
                      </TableCell>
                      <TableCell>
                        <Button
                          size="small"
                          variant="outlined"
                          color="error"
                          startIcon={<Delete />}
                          disabled={purgingOrphanId === user.id}
                          onClick={() => purgeOrphanedUser(user.id, user.email)}
                        >
                          {purgingOrphanId === user.id ? 'Deleting…' : 'Delete'}
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}

      {/* Customer List */}
      <Card sx={{ mt: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            All Customers
          </Typography>
          <TableContainer component={Paper} variant="outlined">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Phone</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Subscription</TableCell>
                  <TableCell>SMS Count (24h)</TableCell>
                  <TableCell>Created</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data?.customers && Array.isArray(data.customers) && data.customers.map((customer) => {
                  const smsInfo = smsCounts[customer.phone_number];
                  const isLoading = loadingSmsCounts[customer.phone_number];
                  
                  return (
                    <TableRow key={customer.id}>
                      <TableCell>{customer.name}</TableCell>
                      <TableCell>{customer.phone_number}</TableCell>
                      <TableCell>{customer.caregiver_email}</TableCell>
                      <TableCell>
                        <Chip
                          label={customer.is_active ? 'Active' : 'Inactive'}
                          color={customer.is_active ? 'success' : 'default'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={getSubscriptionLabel(customer)}
                          color={getSubscriptionChipColor(customer) as any}
                          size="small"
                        />
                        {customer.subscription_valid_until && (
                          <Typography variant="caption" display="block" color="text.secondary">
                            Until: {new Date(customer.subscription_valid_until).toLocaleDateString()}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {isLoading ? (
                          <CircularProgress size={16} />
                        ) : smsInfo ? (
                          <Box>
                            <Typography variant="body2" component="span">
                              <strong>{smsInfo.current_count}</strong>/{smsInfo.rate_limit}
                            </Typography>
                            <Typography variant="caption" display="block" color="text.secondary">
                              {smsInfo.check_in_messages} check-ins, {smsInfo.alert_messages} alerts
                            </Typography>
                            {smsInfo.remaining_messages < 5 && (
                              <Chip
                                label={`${smsInfo.remaining_messages} remaining`}
                                color={smsInfo.remaining_messages === 0 ? 'error' : 'warning'}
                                size="small"
                                sx={{ mt: 0.5 }}
                              />
                            )}
                          </Box>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            -
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {new Date(customer.created_at).toLocaleDateString()}
                      </TableCell>
                      <TableCell>
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        {customer.is_active ? (
                          <>
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<Visibility />}
                              disabled={sendingCheckIn === customer.id}
                              onClick={() => handleSendCheckIn(customer.id)}
                            >
                              {sendingCheckIn === customer.id ? 'Sending...' : 'Send Check-in'}
                            </Button>
                            <Button
                              size="small"
                              variant="outlined"
                              color="warning"
                              onClick={() => openDeactivateDialog(customer)}
                            >
                              Deactivate
                            </Button>
                          </>
                        ) : (
                          <Button
                            size="small"
                            variant="outlined"
                            color="success"
                            onClick={() => handleActivateCustomer(customer)}
                          >
                            Activate
                          </Button>
                        )}
                        {isAdmin && (
                          <Button
                            size="small"
                            variant="outlined"
                            color="error"
                            startIcon={<Delete />}
                            onClick={() => openDeleteDialog(customer)}
                          >
                            Delete
                          </Button>
                        )}
                      </Box>
                      </TableCell>
                    </TableRow>
                  );
                })}
                {(!data?.customers || !Array.isArray(data.customers) || data.customers.length === 0) && (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      No customers found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Add Customer Dialog */}
      <Dialog open={customerDialogOpen} onClose={() => setCustomerDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add New Customer</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Customer Name"
            fullWidth
            variant="outlined"
            value={newCustomer.name}
            onChange={(e) => setNewCustomer({ ...newCustomer, name: e.target.value })}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="Customer Phone Number"
            fullWidth
            variant="outlined"
            value={newCustomer.phone_number}
            onChange={(e) => setNewCustomer({ ...newCustomer, phone_number: e.target.value })}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="Caregiver Name"
            fullWidth
            variant="outlined"
            value={newCustomer.caregiver_name}
            onChange={(e) => setNewCustomer({ ...newCustomer, caregiver_name: e.target.value })}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="Caregiver Phone"
            fullWidth
            variant="outlined"
            value={newCustomer.caregiver_phone}
            onChange={(e) => setNewCustomer({ ...newCustomer, caregiver_phone: e.target.value })}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="Caregiver Email"
            type="email"
            fullWidth
            variant="outlined"
            value={newCustomer.caregiver_email}
            onChange={(e) => setNewCustomer({ ...newCustomer, caregiver_email: e.target.value })}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCustomerDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleCreateCustomer} variant="contained">
            Add Customer
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Customer Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Delete Customer</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the customer "{customerToDelete?.name}"? This action cannot be undone and will permanently remove all data including the user account.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteCustomer} variant="contained" color="error">
            Delete Customer
          </Button>
        </DialogActions>
      </Dialog>

      {/* Deactivate Customer Confirmation Dialog */}
      <Dialog open={deactivateDialogOpen} onClose={() => setDeactivateDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Deactivate Customer</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to deactivate the customer "{customerToDeactivate?.name}"? They will no longer receive daily check-ins, but their data will be preserved and they can be reactivated later.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeactivateDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDeactivateCustomer} variant="contained" color="warning">
            Deactivate Customer
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Dashboard;
